import { Badge, Button, Card, Col, Divider, Row, Space, Typography } from "antd";
import { CopyOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { IGenerateOtpResponse } from "../../../models/otp/response";
import CountdownTimer from "../../general/countdown-timer";
import { successNotification } from "../../../utils/helpers/notification";
import styles from "./style.module.scss";

const { Text, Title } = Typography;

interface OtpResultCardProps {
  result: IGenerateOtpResponse;
}

const OtpResultCard: React.FC<OtpResultCardProps> = ({ result }) => {
  const { t } = useTranslation();

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(result.otp);
      successNotification(t("code_copied"));
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const formatDateTime = (isoString: string): string => {
    const date = new Date(isoString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  return (
    <Card className={styles.otpCard} title={t("otp_code")} bordered={false}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* OTP Code Display */}
        <div className={styles.otpCodeSection}>
          <Title level={1} className={styles.otpCode}>
            {result.otp}
          </Title>
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={handleCopyCode}
            size="large"
          >
            {t("copy_code")}
          </Button>
        </div>

        <Divider />

        {/* Details Section */}
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {/* Mobile Number */}
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              {t("mobile_number")}
            </Text>
            <Text strong style={{ fontSize: "16px" }}>
              {result.mobile}
            </Text>
          </div>

          {/* Countdown Timer - Prominent Display */}
          <div className={styles.timerSection}>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              {t("time_remaining")}
            </Text>
            <div className={styles.countdownDisplay}>
              <CountdownTimer validUntil={result.validUntil} />
            </div>
          </div>

          {/* Valid Until */}
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              {t("valid_until")}
            </Text>
            <Text strong>{formatDateTime(result.validUntil)}</Text>
          </div>

          {/* Message Status */}
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              {t("message_status")}
            </Text>
            {result.messageSent ? (
              <Text style={{ color: "#52c41a" }}>
                <CheckCircleOutlined /> {t("message_sent")}
              </Text>
            ) : (
              <Text type="secondary">
                <CloseCircleOutlined /> {t("message_not_sent")}
              </Text>
            )}
          </div>
        </Space>
      </Space>
    </Card>
  );
};

export default OtpResultCard;
